import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import AITextExtractor, { getInitialMessage } from '../components/AITextExtractor';
import { auth, db } from '../firebase';
import { showToast } from '../utils/showToast';

const freshChat = () => [{ role: 'assistant', content: getInitialMessage('superAdmin') }];

// Don't split a surrogate pair at the cut point - Firestore rejects strings
// containing lone surrogates.
const safeTruncate = (str, max) => {
  let out = str.slice(0, max);
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
};

// Firestore rejects undefined values, so round-trip through JSON and trim
// oversized tool args before saving.
const sanitizeMessages = (messages) =>
  JSON.parse(JSON.stringify(messages, (key, value) => {
    if (key === 'args' && value && typeof value === 'object') {
      const serialized = JSON.stringify(value);
      if (serialized.length > 4000) return { truncated: `${safeTruncate(serialized, 4000)}...` };
    }
    return value;
  }));

// A document caps at 1MB; past ~900KB drop the oldest turns (keeping the
// greeting and the latest exchanges) so a marathon chat keeps saving instead
// of permanently failing on every turn. Drop whole user/assistant pairs (even
// counts) so the saved history keeps alternating roles - it gets replayed to
// Gemini on resume.
const MAX_SAVED_BYTES = 900000;
const trimToDocLimit = (messages) => {
  const trimmed = sanitizeMessages(messages);
  const encoder = new TextEncoder();
  while (trimmed.length > 3 && encoder.encode(JSON.stringify(trimmed)).length > MAX_SAVED_BYTES) {
    trimmed.splice(1, 2 * Math.max(1, Math.ceil(trimmed.length / 10)));
  }
  return trimmed;
};

const formatWhen = (timestamp) => {
  if (!timestamp?.toDate) return '';
  const date = timestamp.toDate();
  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export default function AdminAgent() {
  const user = auth.currentUser;
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState(freshChat);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // null = still resolving the token; saving/listing only works with the
  // superAdmin custom claim (same gate as the agent endpoint itself), so
  // vendor-provisioned operators get a notice instead of permission errors.
  const [hasSuperAdminClaim, setHasSuperAdminClaim] = useState(null);

  const messagesRef = useRef(messages);
  const claimRef = useRef(false);
  // One session object per chat shown in the pane. Each save resolves its
  // target doc from the session captured when the turn's messages arrive, so a
  // write that completes after the admin switches chats can never land in the
  // wrong conversation. epoch keys the extractor so per-chat DOM state (open
  // step details, scroll) resets on switch.
  const sessionRef = useRef({ docId: null, epoch: 0 });

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let timer = null;
    // Retry with backoff instead of latching a failure: a transient
    // getIdTokenResult error (expired cached token + network blip) must not
    // leave a real super admin silently unable to save chats.
    const resolveClaim = (attempt) => {
      user.getIdTokenResult()
        .then((result) => {
          if (cancelled) return;
          claimRef.current = result.claims.superAdmin === true;
          setHasSuperAdminClaim(claimRef.current);
        })
        .catch((err) => {
          console.error('Failed to resolve admin claims:', err);
          if (cancelled) return;
          timer = setTimeout(() => resolveClaim(attempt + 1), Math.min(30000, 2000 * 2 ** attempt));
        });
    };
    resolveClaim(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  useEffect(() => {
    if (!user || hasSuperAdminClaim !== true) return undefined;
    const conversationsQuery = query(
      collection(db, 'agentConversations'),
      where('uid', '==', user.uid),
      orderBy('updatedAt', 'desc'),
      // The whole doc (messages included) downloads for every listed chat, so
      // keep the window modest; older chats simply age off the sidebar.
      limit(50),
    );
    return onSnapshot(
      conversationsQuery,
      (snapshot) => setConversations(
        snapshot.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) })),
      ),
      (err) => {
        console.error('Failed to load saved conversations:', err);
        showToast('Failed to load saved conversations.', { type: 'error' });
      },
    );
  }, [user, hasSuperAdminClaim]);

  const persist = (session, nextMessages) => {
    // Nothing worth saving until the admin has actually sent something.
    if (!user || !claimRef.current || nextMessages.length <= 1) return;
    const payload = { messages: trimToDocLimit(nextMessages), updatedAt: serverTimestamp() };
    let write;
    if (session.docId) {
      write = updateDoc(doc(db, 'agentConversations', session.docId), payload);
    } else {
      // Reserve the id synchronously instead of awaiting addDoc: later saves
      // for this session target the same doc, and the SDK preserves write
      // order on its own, even offline.
      const newRef = doc(collection(db, 'agentConversations'));
      session.docId = newRef.id;
      const firstUserMessage = nextMessages.find((m) => m.role === 'user');
      write = setDoc(newRef, {
        ...payload,
        uid: user.uid,
        title: safeTruncate(firstUserMessage?.content || 'New chat', 80),
        createdAt: serverTimestamp(),
      });
      if (sessionRef.current === session) setActiveId(newRef.id);
    }
    write.catch((err) => {
      if (err?.code === 'not-found') {
        // The doc was deleted from another tab or device. Recreate it with the
        // full in-memory history instead of failing every subsequent turn.
        session.docId = null;
        if (sessionRef.current === session) persist(session, nextMessages);
        return;
      }
      console.error('Failed to save conversation:', err);
      showToast('Failed to save this conversation.', { type: 'error' });
    });
  };

  const handleMessagesChange = (updater) => {
    const next = typeof updater === 'function' ? updater(messagesRef.current) : updater;
    messagesRef.current = next;
    setMessages(next);
    persist(sessionRef.current, next);
  };

  const openSession = (docId, sessionMessages) => {
    sessionRef.current = { docId, epoch: sessionRef.current.epoch + 1 };
    setActiveId(docId);
    messagesRef.current = sessionMessages;
    setMessages(sessionMessages);
    setIsSidebarOpen(false);
  };

  const handleNewChat = () => {
    if (isProcessing) return;
    openSession(null, freshChat());
  };

  const handleSelectConversation = (conversation) => {
    if (isProcessing) return;
    setIsSidebarOpen(false);
    if (conversation.id === sessionRef.current.docId) return;
    openSession(
      conversation.id,
      Array.isArray(conversation.messages) && conversation.messages.length > 0
        ? conversation.messages
        : freshChat(),
    );
  };

  const handleDelete = (e, conversation) => {
    e.stopPropagation();
    if (isProcessing) return;
    if (!window.confirm(`Delete "${conversation.title || 'this chat'}"?`)) return;
    // Fire-and-forget: the SDK applies the delete locally right away (and
    // queues it offline), so don't hold the session reset hostage to the
    // server ack - that window is exactly where stale-session bugs live.
    deleteDoc(doc(db, 'agentConversations', conversation.id)).catch((err) => {
      console.error('Failed to delete conversation:', err);
      showToast('Failed to delete conversation.', { type: 'error' });
    });
    if (conversation.id === sessionRef.current.docId) {
      openSession(null, freshChat());
    }
  };

  const sidebarContent = (
    <>
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={handleNewChat}
          disabled={isProcessing}
          className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 disabled:opacity-50"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {hasSuperAdminClaim === null ? (
          <p className="p-3 text-xs text-gray-400 dark:text-gray-500">Loading saved chats...</p>
        ) : hasSuperAdminClaim === false ? (
          <p className="p-3 text-xs text-gray-400 dark:text-gray-500">Saved chats require super-admin access.</p>
        ) : conversations.length === 0 ? (
          <p className="p-3 text-xs text-gray-400 dark:text-gray-500">
            No saved chats yet. Conversations are saved automatically after your first message.
          </p>
        ) : (
          conversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => handleSelectConversation(conversation)}
              className={`group flex items-start justify-between px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-800 ${
                conversation.id === activeId ? 'bg-primary bg-opacity-10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              } ${isProcessing ? 'pointer-events-none opacity-60' : ''}`}
            >
              <div className="min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-200 truncate">{conversation.title || 'Untitled chat'}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{formatWhen(conversation.updatedAt)}</p>
              </div>
              <button
                onClick={(e) => handleDelete(e, conversation)}
                aria-label="Delete chat"
                className="ml-2 mt-0.5 flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-96px)]">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Admin AI Assistant</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
            Query your events using natural language. The assistant has access to the events collection and can update a few fields.
          </p>
        </div>
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="md:hidden flex-shrink-0 flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chats
        </button>
      </div>
      <div className="flex flex-1 min-h-0 md:gap-4">
        <Dialog open={isSidebarOpen} onClose={setIsSidebarOpen} className="relative z-50 md:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />
          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative flex w-72 max-w-[80vw] flex-col bg-white dark:bg-gray-800 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <div className="flex items-center justify-between px-3 pt-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Chats</span>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  aria-label="Close chat list"
                  className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {sidebarContent}
            </DialogPanel>
          </div>
        </Dialog>
        <aside className="hidden md:flex w-64 flex-shrink-0 flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
          {sidebarContent}
        </aside>
        <div className="flex-1 min-w-0 min-h-0">
          <AITextExtractor
            key={`chat-${sessionRef.current.epoch}`}
            type="superAdmin"
            fullHeight
            messages={messages}
            onMessagesChange={handleMessagesChange}
            onNewChat={handleNewChat}
            onProcessingChange={setIsProcessing}
          />
        </div>
      </div>
    </div>
  );
}
